import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  ScrollView,
} from 'react-native';
import { apiService } from '../services/apiService';

interface ExposureItem {
  name: string;
  value: number;
  color: string;
  filterType?: string;
}

interface TopExposureProps {
  items: ExposureItem[];
  onDataChange?: (items: ExposureItem[]) => void;
  onExposurePress?: (underlyingName: string) => void;
  ownershipScope?: 'group' | 'self';
}

type TabType = 'worst' | 'equally';

export const TopExposure: React.FC<TopExposureProps> = ({
  items: initialItems,
  onDataChange,
  onExposurePress,
  ownershipScope = 'group',
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('worst');
  const [items, setItems] = useState<ExposureItem[]>(initialItems);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [allItems, setAllItems] = useState<ExposureItem[]>([]);
  const requestIdRef = useRef(0);
  const switchingRef = useRef(false);

  // Fetch data when tab changes
  const fetchDataForTab = async (tabType: TabType) => {
    try {
      // Incrementa el requestId para invalidar respuestas anteriores
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setLoading(true);
      console.log('🔍 TOP EXPOSURE - Fetching data for tab:', tabType);

      const data = await apiService.getTopUnderlyingExposure(tabType, false, ownershipScope);
      console.log('🔍 TOP EXPOSURE - Received data:', data.length, 'items');

      // Ignora respuestas obsoletas si el usuario cambió de pestaña durante la carga
      if (requestId !== requestIdRef.current) {
        return;
      }

      setItems(data || []);
      setAllItems(data || []); // Store all items for modal
      onDataChange?.(data || []);
    } catch (error) {
      console.error('❌ TOP EXPOSURE - Error fetching data for tab:', tabType, error);
      // On error, set empty array to prevent showing stale data
      // Solo limpia si la petición aún es la vigente
      if (requestIdRef.current) {
        setItems([]);
        setAllItems([]);
      }
    } finally {
      // Evita apagar el loading si ya se disparó otra petición
      if (requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Handle tab change
  const handleTabChange = (tabType: TabType) => {
    if (tabType === activeTab) return;
    if (switchingRef.current) return; // evita doble click rápido
    switchingRef.current = true;
    console.log('🔄 TOP EXPOSURE - Changing tab from', activeTab, 'to', tabType);
    setActiveTab(tabType);
    fetchDataForTab(tabType);
    setTimeout(() => {
      switchingRef.current = false;
    }, 250);
  };

  // Load initial data
  useEffect(() => {
    if (initialItems.length === 0) {
      fetchDataForTab(activeTab);
    } else {
      // If we have initial items, set them as all items too
      setAllItems(initialItems);
    }
  }, [ownershipScope]);

  // Load data when modal opens if we don't have data
  useEffect(() => {
    if (modalVisible && allItems.length === 0) {
      fetchDataForTab(activeTab);
    }
  }, [modalVisible]);

  // Use current items for display (Top 10 for desktop, Top 3 for mobile)
  const displayItems = items.slice(0, 10);
  const max = items.length > 0 ? Math.max(...items.map((item) => item.value)) : 0;
  const modalMax = allItems.length > 0 ? Math.max(...allItems.map((item) => item.value)) : 0;

  const tabs = [
    { key: 'worst' as TabType, label: 'Worst of' },
    { key: 'equally' as TabType, label: 'Equally-Weighted' },
  ];

  return (
    <View>
      {/* Tabs */}
      <View style={styles.tabsContainer}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          console.log(`Tab ${tab.key}: isActive=${isActive}, activeTab=${activeTab}`);
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, isActive && styles.activeTab]}
              onPress={() => handleTabChange(tab.key)}
              disabled={loading}
            >
              <Text style={[styles.tabText, isActive && styles.activeTabText]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content */}
      <View style={styles.content}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#3B82F6" />
            <Text style={styles.loadingText}>Loading data...</Text>
          </View>
        ) : displayItems.length > 0 ? (
          displayItems.map((item, idx) => (
            <TouchableOpacity 
              key={`${item.name}-${idx}`} 
              style={styles.exposureItem}
              onPress={() => onExposurePress?.(item.name)}
              activeOpacity={0.7}
            >
              <Text style={styles.exposureName}>{item.name}</Text>
              <Text style={styles.exposureValue}>{item.value.toLocaleString()}%</Text>
              <View style={styles.exposureBar}>
                <View
                  style={[
                    styles.exposureFill,
                    {
                      backgroundColor: item.color,
                      width: `${max > 0 ? (item.value / max) * 100 : 0}%`,
                    },
                  ]}
                />
              </View>
            </TouchableOpacity>
          ))
        ) : (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>No data available for this view</Text>
          </View>
        )}

        {/* See more link */}
        {items.length > 10 && (
          <View style={styles.seeMoreContainer}>
            <TouchableOpacity
              onPress={() => {
                console.log('See more pressed, items length:', items.length);
                requestAnimationFrame(() => {
                  setTimeout(() => setModalVisible(true), 100);
                });
              }}
            >
              <Text style={styles.seeMoreText}>See more ({items.length - 10} more)</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Modal for all items */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
        onDismiss={() => setModalVisible(false)}
        presentationStyle="overFullScreen"
        statusBarTranslucent
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setModalVisible(false)}
          />
          <View style={styles.modalContent}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Top Underlying Exposure</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setModalVisible(false)}
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Modal Tabs */}
            <View style={styles.modalTabsContainer}>
              {tabs.map((tab) => (
                <TouchableOpacity
                  key={tab.key}
                  style={[styles.modalTab, activeTab === tab.key && styles.modalActiveTab]}
                  onPress={() => handleTabChange(tab.key)}
                  disabled={loading}
                >
                  <Text
                    style={[
                      styles.modalTabText,
                      activeTab === tab.key && styles.modalActiveTabText,
                    ]}
                  >
                    {tab.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Modal Content */}
            <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={false}>
              {loading ? (
                <View style={styles.modalLoadingContainer}>
                  <ActivityIndicator size="small" color="#3B82F6" />
                  <Text style={styles.modalLoadingText}>Loading data...</Text>
                </View>
              ) : allItems.length > 0 ? (
                allItems.map((item, idx) => (
                  <TouchableOpacity 
                    key={`${item.name}-${idx}`} 
                    style={styles.modalExposureItem}
                    onPress={() => {
                      setModalVisible(false);
                      onExposurePress?.(item.name);
                    }}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.modalExposureName}>{item.name}</Text>
                    <Text style={styles.modalExposureValue}>{item.value.toLocaleString()}%</Text>
                    <View style={styles.modalExposureBar}>
                      <View
                        style={[
                          styles.modalExposureFill,
                          {
                            backgroundColor: item.color,
                            width: `${modalMax > 0 ? (item.value / modalMax) * 100 : 0}%`,
                          },
                        ]}
                      />
                    </View>
                  </TouchableOpacity>
                ))
              ) : (
                <View style={styles.modalEmptyState}>
                  <Text style={styles.modalEmptyStateText}>No data available for this view</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  tabsContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    padding: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  activeTab: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  activeTabText: {
    color: '#1F2937',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  content: {
    marginTop: 8,
  },
  exposureItem: {
    marginBottom: 16,
  },
  exposureName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
    fontFamily: 'Montserrat_600SemiBold',
  },
  exposureValue: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 6,
    fontFamily: 'Montserrat_400Regular',
  },
  exposureBar: {
    height: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 3,
    overflow: 'hidden',
  },
  exposureFill: {
    height: '100%',
    borderRadius: 3,
  },
  seeMoreContainer: {
    alignItems: 'flex-end',
    marginTop: 8,
  },
  seeMoreText: {
    fontSize: 12,
    color: '#6B7280',
    textDecorationLine: 'underline',
    fontFamily: 'Montserrat_400Regular',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6B7280',
    fontStyle: 'italic',
    fontFamily: 'Montserrat_400Regular',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    minHeight: '60%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  modalTabsContainer: {
    flexDirection: 'row',
    marginHorizontal: 20,
    marginVertical: 16,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    padding: 4,
  },
  modalTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  modalActiveTab: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  modalTabText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  modalActiveTabText: {
    color: '#1F2937',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  modalScrollView: {
    flex: 1,
    paddingHorizontal: 20,
  },
  modalLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  modalLoadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  modalExposureItem: {
    marginBottom: 12,
  },
  modalExposureName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
    fontFamily: 'Montserrat_600SemiBold',
  },
  modalExposureValue: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 6,
    fontFamily: 'Montserrat_400Regular',
  },
  modalExposureBar: {
    height: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 3,
    overflow: 'hidden',
  },
  modalExposureFill: {
    height: '100%',
    borderRadius: 3,
  },
  modalEmptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  modalEmptyStateText: {
    fontSize: 14,
    color: '#6B7280',
    fontStyle: 'italic',
    fontFamily: 'Montserrat_400Regular',
  },
});
